import React, { useEffect, useState } from "react";
import styles from "./ProfileSubheader.module.scss";
import isEmpty from "lodash/isEmpty";
import { updateTag } from "../../../redux/slices/tagSlice";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../types/types";
import { ReactComponent as Pipe } from "../../../assets/images/pipe.svg";
import { getGradientBackground } from "../../../utils/getGradientBackground";
import { useTranslation } from "react-i18next";
import {
  getAwardIcon,
  getCountryNameFromCode,
  getAwardNameFromCode,
} from "../../../utils/awardIconMapping";
import { getPerformanceTag } from "../../../utils/performanceTagUtil";
import {
  useGetAboSummaryDetailsQuery,
  useGetAboAccountManagerQuery,
  useGetAboPerformanceQuery,
  useGetAboAwardQuery,
  useGetABOLOADetailsQuery,
  useGetUplineDiamondInfoQuery,
} from "../../../redux/services/aboInfoBannerDetailsAPI";
import { useProfileParams } from "../../../utils/common";
import { useNavigate } from "react-router-dom";
import { setCurrencyCode } from "../../../redux/slices/CurrencyCodeSlice";

interface ProfileSubHeaderProps {
  onAwardCodeChange: (data: string) => void;
}

const ProfileSubHeader: React.FC<ProfileSubHeaderProps> = ({
  onAwardCodeChange,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const userDetails = useSelector((state: any) => state.userDetails.user);
  const currentTag = useSelector((state: RootState) => state.tag.currentTag);
  const currentTheme = useSelector(
    (state: RootState) => state.theme.currentTheme
  );

  const [AboAwardData, setAboAwardData] = useState<ABOAwardDataType | null>(
    null
  );

  const [aboPerformanceTag, setAboPerformanceTag] =
    useState<ABOPerformanceDataType | null>(null);
  const [summaryDetails, setSummaryDetails] =
    useState<ABOSummaryDataType | null>(null);

  const { affiliateCode, aboNumber } = useProfileParams();

  //Summary details API
  const {
    data: aboSummaryDetails,
    isLoading: aboSummaryLoading,
    isError: aboSummaryError,
  } = useGetAboSummaryDetailsQuery(
    {
      affiliateCode,
      aboNumber,
    },
    { refetchOnMountOrArgChange: true }
  );

  //Account manager API - depends on the Summary details API for the native id.
  const {
    data: aboAccountManager,
    isLoading: aboAccountManagerLoading,
    isError: aboAccountManagerError,
  } = useGetAboAccountManagerQuery(
    { nativeId: aboSummaryDetails?.body?.accountManagerNativeId },
    {
      skip: isEmpty(aboSummaryDetails?.body?.accountManagerNativeId),
      refetchOnMountOrArgChange: true,
    }
  );
  const currencyCode = aboSummaryDetails?.body?.currencyCode ?? "USD";
  useEffect(() => {
    if (currencyCode) {
      dispatch(setCurrencyCode(currencyCode));
    }
  }, [currencyCode, dispatch]);

  const accountManagerDetails: ABOAccountManagerDataType = isEmpty(
    aboSummaryDetails?.body?.accountManagerNativeId
  )
    ? null
    : aboAccountManager?.body;

  const { data: aboLOADetails, isLoading: isLoadingLOA } =
    useGetABOLOADetailsQuery(
      {
        affiliateCode,
        aboNumber,
      },
      { refetchOnMountOrArgChange: true }
    );

  //Award API
  const {
    isLoading: isLoadingAward,
    isError: isAwardError,
    data: awardData,
  } = useGetAboAwardQuery(
    {
      affiliateCode,
      aboNumber,
    },
    { refetchOnMountOrArgChange: true }
  );

  //ABO performance API
  const {
    isLoading: isLoadingPerformance,
    isError: isPerformanceError,
    data: performanceData,
  } = useGetAboPerformanceQuery(
    {
      affiliateCode,
      aboNumber,
    },
    { refetchOnMountOrArgChange: true }
  );

  // Upline Diamond API
  const {
    data: uplineDiamondInfo,
    isLoading: isLoadingUplineInfo,
    isError: isUplineInfoError,
  } = useGetUplineDiamondInfoQuery(
    {
      affiliateCode,
      aboNumber,
    },
    { refetchOnMountOrArgChange: true }
  );

  const background = getGradientBackground({ tag: currentTag });

  const ABOBackground =
    currentTheme === "light" ? background.ABOBackground : "#282828";

  useEffect(() => {
    if (aboSummaryDetails) {
      setSummaryDetails(aboSummaryDetails?.body ?? {});
      localStorage.setItem("subHeaderName", aboSummaryDetails?.body?.name);
    }
  }, [aboSummaryDetails]);

  useEffect(() => {
    setAboAwardData(awardData?.body ?? {});
    onAwardCodeChange(awardData?.body?.currentAwardCode ?? null);
    localStorage.setItem("currentAward", awardData?.body?.currentAwardName);
  }, [awardData]);

  useEffect(() => {
    setAboPerformanceTag(performanceData?.body ?? {});
  }, [performanceData]);

  useEffect(() => {
    dispatch(
      updateTag(
        getPerformanceTag({
          tagValue: aboPerformanceTag?.aboPerformance ?? undefined,
        })
      )
    );
  }, [dispatch, aboPerformanceTag, currentTheme]);
  const trackingPin = getAwardNameFromCode(AboAwardData?.trackingAwardCode, t);
  const currentPin = getAwardNameFromCode(AboAwardData?.currentAwardCode, t);
  const accountManager = accountManagerDetails?.fullName;
  const nativeId = accountManagerDetails?.nativeId;
  const loaName = aboLOADetails?.body?.loaName || "";
  const uplineDiamond = uplineDiamondInfo?.body?.name || "";
  const isLoaSubheaderVisible = summaryDetails?.marketCountryCode !== "IN";

  const handleClick = () => {
    navigate(`/assignment/${affiliateCode}/${aboNumber}/${1}`); // role 1 for Account Manager
  };

  return (
    <section className={styles.profile_container}>
      {aboSummaryLoading &&
        isLoadingAward &&
        isLoadingPerformance &&
        isLoadingLOA &&
        isLoadingUplineInfo ? (
        <div className={`${styles.profile_wrapper} animate`}></div>
      ) : aboSummaryError ||
        aboAccountManagerError ||
        isAwardError ||
        isPerformanceError ||
        isUplineInfoError ? (
        <div data-testId="error_testID" className={styles.error_text}>
          {t("no_info_available")}
        </div>
      ) : (
        <div
          className={`profile_wrapper ${styles.profile_wrapper}`}
        >
          <div
            className={styles.side_gredient}
          ></div>
          <div
            className={`profile_inner_wrapper ${styles.profile_inner_wrapper}`}
          >
            <div className={styles.profile_main_info_wrapper}>
              <div className={styles.abo_info_name_number}>
                <div className={`abo_name ${styles.abo_name}`}>
                  {summaryDetails?.localName}
                </div>
                <div className={styles.abo_number_status}>
                  <span className={`abo_number ${styles.abo_number}`}>
                    #{summaryDetails?.aboNumber}
                  </span>
                  {summaryDetails?.marketCountryCode && (
                    <span className={styles.pipeIcon}>
                      <Pipe />
                    </span>
                  )}
                  <span className={`country ${styles.country}`}>
                    {getCountryNameFromCode(
                      summaryDetails?.marketCountryCode,
                      t
                    )}
                  </span>
                  {/* <span className={styles.abo_status}> //TODO: Uncomment the code if need to show performance tag
                  <StatusTag />
                </span> */}
                </div>
                <div
                  className={`account_name_wrapper  ${styles.account_name_wrapper}`}
                >
                  {aboAccountManagerLoading ? null : accountManager ? (
                    <>
                      <span
                        className={`account_name ${styles.account_name}`}
                        data-testid="account_manager_testID"
                      >
                        {t("sub_header_info.accountManagerName")}:
                      </span>
                      <span
                        className={`account_name_value ${styles.account_name_value}`}
                        title={accountManager || nativeId || ""}
                      >
                        {accountManager || nativeId || "-"}{" "}
                        <span
                          className={`assign_name ${styles.assign_name}`}
                          onClick={handleClick}
                        >
                          {t("Change")}
                        </span>
                      </span>
                    </>
                  ) : (
                    <span
                      onClick={handleClick}
                      className={`assign_name ${styles.assign_name}`}
                      data-testid="assign_name_testID"
                    >
                      {t("assign_account_manager")}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {/* <div className={styles.info_wrapper}> */}
            <div className={styles.abo_info_account_pin}>
              <div
                className={`field_title_wrapper ${styles.field_title_wrapper}`}
              >
                <span
                  className={`${styles.data_item} ${styles.upline_name}`}
                  data-testid="account_manager_testID"
                >
                  {" "}
                  {t("sub_header_info.uplinePinDiamond")}
                </span>
                {isLoaSubheaderVisible && (
                  <span
                    className={`${styles.data_item}  ${styles.loaName}`}
                    data-testid="loa_name_testID"
                  >
                    {t("sub_header_info.loaName")}
                  </span>
                )}
                <span
                  className={`${styles.data_item} ${styles.trackingPin}`}
                  data-testid="tracking_pin_testID"
                >
                  {t("sub_header_info.trackingPin")}
                </span>
                <span
                  className={`${styles.data_item} ${styles.currentPin}`}
                  data-testid="current_pin_testID"
                >
                  {t("sub_header_info.currentPin")}
                </span>
              </div>
              <div
                className={`field_data_wrapper ${styles.field_data_wrapper}`}
              >
                <span
                  className={`upline_name ${styles.data_item} ${styles.data_value} ${uplineDiamond && styles.upline_name}`}
                  title={uplineDiamond ? uplineDiamond : ""}
                >
                  {uplineDiamond ? uplineDiamond : "-"}
                </span>
                {isLoaSubheaderVisible && (
                  <span
                    className={`loaName ${styles.data_item} ${styles.data_value} ${loaName && styles.loaName}`}
                    title={loaName ? loaName : ""}
                  >
                    {loaName ? loaName : "-"}
                  </span>
                )}

                <div
                  className={`${styles.data_item} ${styles.data_value} ${styles.trackingPin_wrapper}`}
                >
                  <span className={styles.awardIcon}>
                    {getAwardIcon(AboAwardData?.trackingAwardName)}
                  </span>
                  <div
                    className={`${styles.trackingPin} ${styles.values}`}
                    title={trackingPin}
                  >
                    {trackingPin}
                  </div>
                </div>
                <div
                  className={`${styles.data_item} ${styles.data_value} ${styles.currentPin_wrapper}`}
                >
                  <span className={styles.awardIcon}>
                    {getAwardIcon(AboAwardData?.currentAwardName)}
                  </span>
                  <div
                    className={`${styles.currentPin} ${styles.values}`}
                    title={currentPin}
                    data-custom-id={getAwardNameFromCode(AboAwardData?.currentAwardCode, t, 'en')}
                  >
                    {currentPin}
                  </div>
                </div>
              </div>
            </div>
            {/* </div> */}
          </div>
        </div>
      )}
    </section>
  );
};

export default ProfileSubHeader;
